import React from 'react';

import makeStyles from '@mui/styles/makeStyles';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Snackbar from '@mui/material/Snackbar';

import { NotifyProvider } from './contexts/Notify';
import * as auth0 from './utils/auth0';
import useInterval from './hooks/useInterval';
import ChannelList from './misc/ChannelList';
import AirchainAPI from './utils/airchain-api';
import I18n from './I18n';
import Header from './Header';
import * as M from './utils/metadata';
import Restreamer from './utils/restreamer';
import Router from './Router';
import Views from './views';
import { UI as Version } from './version';
import Changelog from './misc/Changelog';

import SemverGt from 'semver/functions/gt';
import SemverValid from 'semver/functions/valid';

const useStyles = makeStyles((theme) => ({
	MainHeader: {
		height: '132px',
	},
	// todo: one layer
	MainContent: {
		height: '100%',
		'& .MainContent-container': {
			minHeight: 'calc(100vh - 230px)',
		},
		'& .MainContent-item': {
			maxWidth: '980px',
		},
	},
}));

export default function RestreamerUI(props) {
	const classes = useStyles();

	const [$state, setState] = React.useState({
		initialized: false,
		valid: false,
		connected: false,
		compatibility: { compatible: false },
		ingest: false,
		password: false,
		updates: false,
		service: false,
		// Signed in with this instance's own credentials rather than an
		// identity provider. Gates the System sections of Settings; see
		// Restreamer.IsOperator().
		isOperator: false,
		// Recognised tenant member (owner or member). They are not operators,
		// but Settings has a Team section that is theirs -- so they are allowed
		// through the door to see it.
		isTeamMember: false,
	});
	const [$ready, setReady] = React.useState(false);
	const [$snack, setSnack] = React.useState({
		open: false,
		message: '',
		severity: 'info',
	});
	const [$channelList, setChannelList] = React.useState({
		open: false,
		channelid: '',
		channels: [],
	});
	// CHANNEL_LIMIT_PLAN.md Phase 3: atLimit defaults to false (fail open --
	// this is advisory UI only, a network hiccup must never block a
	// legitimate "Add channel" click; see control_service.py's
	// t_channel_limit docstring for the same fail-open posture server-side).
	// Refreshed each time the channel drawer opens (handleChannelList)
	// rather than on a standing interval -- the button it gates is only
	// ever visible while that drawer is open.
	const [$channelLimit, setChannelLimit] = React.useState({ atLimit: false });
	const [$metadata, setMetadata] = React.useState({});
	const [$changelog, setChangelog] = React.useState({
		open: false,
		current: '',
		previous: '',
	});

	const restreamer = React.useRef(null);

	React.useEffect(() => {
		(async () => {
			await handleMount();
		})();

		return () => {};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useInterval(() => {
		setState({
			...$state,
			updates: restreamer.current.HasUpdates(),
		});
	}, 1000 * 60);

	const notify = (severity, type, message) => {
		setSnack({
			...$snack,
			open: true,
			message: message,
			severity: severity,
		});

		if (severity === 'success') {
			if (type === 'save:ingest') {
				setState({
					...$state,
					ingest: true,
				});
			}
		} else if (severity === 'error') {
			if (type === 'network') {
				setState({
					...$state,
					initialized: true,
					valid: false,
				});
			} else if (type === 'auth') {
				(async () => {
					await handleLogout();
				})();
			}
		}
	};

	const handleMount = async () => {
		restreamer.current = new Restreamer(props.address);
		restreamer.current.AddListener((event) => {
			notify(event.severity, event.type, event.message);
		});

		// Try if there's still an auth0 session
		if (auth0.init() === true) {
			if (await auth0.isAuthenticated()) {
				const token = await auth0.getToken();
				await restreamer.current.LoginWithToken(token);
			} else {
				const result = await auth0.handleRedirectCallback();
				if (result.initialized === true) {
					if (result.error === true) {
						notify('error', 'auth0', 'Auth0: ' + result.description);
					}
				}
			}
		}

		const valid = await restreamer.current.Validate();

		await checkChangelog();

		// Whether this identity belongs to this instance decides what the
		// header offers: an operator gets System, a tenant user gets Team.
		let teamMember = false;
		try {
			const team = await AirchainAPI.GetMembers(restreamer.current.GetAccessToken());
			teamMember = !!(team && team.me);
		} catch (error) {
			// no token yet, or not a member of this instance
		}

		setState({
			...$state,
			initialized: true,
			valid: valid,
			connected: restreamer.current.IsConnected(),
			compatibility: restreamer.current.Compatibility(),
			ingest: restreamer.current.HasIngest(),
			password: restreamer.current.Auths().length === 0 && !restreamer.current.ConfigOverrides('api.auth.enable'),
			updates: restreamer.current.HasUpdates(),
			service: restreamer.current.HasService(),
			isOperator: restreamer.current.IsOperator(),
			isTeamMember: teamMember,
		});

		setReady(true);
	};

	const checkChangelog = async () => {
		let showChangelog = true;

		if (restreamer.current.IsConnected() === true) {
			let metadata = await restreamer.current.GetMetadata(false);
			const channels = await restreamer.current.ListChannels();
			let current = Version.replace('restreamer-', '');
			let previous = '';

			if (SemverValid(current) === null) {
				showChangelog = false;
			}

			if (metadata === null) {
				if (channels.length === 1) {
					const progress = await restreamer.current.GetIngestProgress(channels[0].channelid);
					if (progress.valid === false) {
						// assume fresh installation
						metadata = M.initMetadata(metadata);
						await restreamer.current.SetMetadata({
							...metadata,
							bundle: {
								...metadata.bundle,
								version: current,
							},
						});
						return false;
					}
				}
			}

			metadata = M.initMetadata(metadata);

			if ('version' in metadata.bundle) {
				if (SemverValid(metadata.bundle.version) !== null) {
					previous = metadata.bundle.version;
				}
			}

			if (showChangelog === true) {
				if (SemverValid(previous) === null) {
					previous = '';
				}

				if (previous.length !== 0) {
					if (!SemverGt(current, previous)) {
						showChangelog = false;
					}
				}
			}

			setMetadata({
				...$metadata,
				...metadata,
			});

			setChangelog({
				...$changelog,
				open: showChangelog,
				current: current,
				previous: previous,
			});
		}

		return showChangelog;
	};

	const handleCloseChangelog = async () => {
		await restreamer.current.SetMetadata({
			...$metadata,
			bundle: {
				...$metadata.bundle,
				version: $changelog.current,
			},
		});

		setChangelog({
			...$changelog,
			open: false,
		});
	};

	const handleLogin = async (username, password) => {
		const connected = await restreamer.current.Login(username, password);

		await checkChangelog();

		setState({
			...$state,
			connected: connected,
			compatibility: restreamer.current.Compatibility(),
			ingest: restreamer.current.HasIngest(),
		});

		return connected;
	};

	const handleAuth0 = async () => {
		const token = await auth0.getToken();
		const connected = await restreamer.current.LoginWithToken(token);

		// Auth0 accepting a sign-in says nothing about whether this instance
		// accepts the account: Core checks the token's subject against its own
		// allowlist and answers 401 "user not allowed" for everyone else. The
		// client swallows that inside LoginWithToken, so without this the app
		// just sits on the login view looking broken after a successful signup.
		if (connected === false) {
			notify('error', 'auth0', 'This account is not authorised on this instance.');
		}

		setState({
			...$state,
			connected: connected,
			compatibility: restreamer.current.Compatibility(),
		});
	};

	const handleLogout = async () => {
		setState({
			...$state,
			initialized: false,
			connected: false,
		});

		restreamer.current.Logout();

		if (await auth0.isAuthenticated()) {
			// logout() sends the browser to Auth0 to end the session, so nothing
			// after it runs: re-validating here would race that navigation and,
			// worse, could start a fresh login from the cookie we are in the
			// middle of clearing.
			await auth0.logout();
			return;
		}

		restreamer.current.Reset();

		const valid = await restreamer.current.Validate();

		setState({
			...$state,
			initialized: true,
			valid: valid,
			connected: restreamer.current.IsConnected(),
			compatibility: restreamer.current.Compatibility(),
			ingest: restreamer.current.HasIngest(),
		});
	};

	const handlePasswordReset = async (username, loginUsername, password, loginPassword) => {
		const data = {
			api: {
				auth: {
					enable: true,
				},
			},
			version: 3,
		};

		if (username.length !== 0) {
			data.api.auth.username = username;
		}

		if (password.length !== 0) {
			data.api.auth.password = password;
		}

		const [, err] = await restreamer.current.ConfigSet(data);
		if (err !== null) {
			notify('error', 'save:settings', `There was an error resetting the password.`);
			return 'ERROR';
		}

		const res = await restreamer.current.ConfigReload();
		if (res === false) {
			notify('error', 'restart', `Restarting the application failed.`);
			return 'ERROR';
		}

		restreamer.current.IgnoreAPIErrors(true);

		const waitFor = (ms) => {
			return new Promise((resolve) => {
				setTimeout(resolve, ms);
			});
		};

		let restarted = false;
		const key = restreamer.current.CreatedAt().toISOString();

		for (let retries = 0; retries <= 60; retries++) {
			await waitFor(1000);

			const about = await restreamer.current.About();
			if (about === null) {
				// Restarted API not yet available
				continue;
			}

			if (about.created_at?.toISOString() === key) {
				// API did not yet restart
				continue;
			}

			restarted = true;
			break;
		}

		if (restarted === true) {
			// After the restart the API requires a login and this means the restart happened
			await restreamer.current.Validate();
			await restreamer.current.Login(loginUsername, loginPassword);

			window.location.reload();
		} else {
			return 'TIMEOUT';
		}

		return 'OK';
	};

	const handlePlayersite = () => {
		document.location.hash = '#/playersite';
	};

	const handleSettings = () => {
		document.location.hash = '#/settings';
	};

	const handleChannelList = () => {
		const channelid = restreamer.current.GetCurrentChannelID();
		const channels = restreamer.current.ListChannels();

		setChannelList({
			...$channelList,
			open: true,
			channelid: channelid,
			channels: channels,
		});

		// Fire-and-forget -- fail-open default ($channelLimit's own initial
		// state) already covers "haven't heard back yet" and "the call
		// failed", so the drawer never waits on this before opening.
		//
		// Regression note: the backend's JSON field is at_limit (snake_case
		// -- AirchainAPI never applies a camelCase-conversion layer, every
		// other field from this API is consumed as raw snake_case
		// elsewhere, e.g. Captions.js's result.checkout_url), but this
		// local state uses atLimit. Storing `result` directly here once
		// shipped with `.atLimit` always undefined -- the button never
		// actually disabled under any real condition. Normalize explicitly
		// rather than storing the raw response, so this can't silently
		// drift again if the backend adds fields later.
		AirchainAPI.GetChannelLimit(restreamer.current.GetAccessToken())
			.then((result) => setChannelLimit({ atLimit: !!result.at_limit }))
			.catch(() => setChannelLimit({ atLimit: false }));
	};

	const handleSelectChannel = (channelid) => {
		restreamer.current.SelectChannel(channelid);
		handleChannelList();

		document.location.hash = `#/${channelid}`;
	};

	const handleCloseChannelList = () => {
		setChannelList({
			...$channelList,
			open: false,
		});
	};

	const handleAddChannel = (name) => {
		const channelid = restreamer.current.CreateChannel(name);
		restreamer.current.SelectChannel(channelid);

		setChannelList({
			...$channelList,
			open: false,
		});

		document.location.hash = `#/${channelid}/edit/wizard`;
	};

	const handleStateChannel = async (channelids) => {
		const processes = await restreamer.current.ListProcesses(['state'], channelids);
		const states = {};

		for (let p of processes) {
			states[p.id] = p.progress.state;
		}

		return states;
	};

	const handleCloseSnack = () => {
		setSnack({
			...$snack,
			open: false,
		});
	};

	if ($ready === false) {
		return (
			<Backdrop open={true}>
				<CircularProgress color="inherit" />
			</Backdrop>
		);
	}

	let view = null;
	if ($state.initialized === false) {
		view = <Views.Initializing />;
	} else {
		if ($state.valid === false) {
			view = <Views.Invalid address={restreamer.current.Address()} />;
		} else if ($state.connected === false) {
			view = (
				<Views.Login
					onLogin={handleLogin}
					auths={restreamer.current.Auths()}
					hasService={$state.service}
					address={restreamer.current.Address()}
					onAuth0={handleAuth0}
				/>
			);
		} else if ($state.compatibility.compatible === false) {
			if ($state.compatibility.core.compatible === false) {
				view = <Views.Incompatible type="core" have={$state.compatibility.core.have} want={$state.compatibility.core.want} />;
			} else if ($state.compatibility.ffmpeg.compatible === false) {
				view = <Views.Incompatible type="ffmpeg" have={$state.compatibility.ffmpeg.have} want={$state.compatibility.ffmpeg.want} />;
			}
		} else if ($state.password === true) {
			view = (
				<Views.Password
					onReset={handlePasswordReset}
					username={restreamer.current.ConfigValue('api.auth.username')}
					usernameOverride={restreamer.current.ConfigOverrides('api.auth.username')}
					password={restreamer.current.ConfigValue('api.auth.password')}
					passwordOverride={restreamer.current.ConfigOverrides('api.auth.password')}
				/>
			);
		} else {
			view = <Router restreamer={restreamer.current} />;
		}
	}

	const expand = $state.connected && $state.compatibility.compatible && !$state.password;

	return (
		<I18n>
			<NotifyProvider value={{ Dispatch: notify }}>
				<Grid container direction="column" justifyContent="flex-start" alignItems="stretch" spacing={0}>
					<Grid className={classes.MainHeader}>
						<Header
							expand={expand}
							showPlayersite={$state.ingest}
							// The System screen shows core logs, ffmpeg internals and the
							// raw config. Gating it on compatibility.compatible answered
							// "is this core version supported", not "who is asking", so
							// every signed-in tenant user saw it -- machinery PRODUCT.md
							// Principle 1 says must not reach customers. Compatibility
							// still gates it too: the screen is useless on an old core.
							showSettings={
								$state.compatibility.compatible && ($state.isOperator || $state.isTeamMember)
							}
							settingsLabel={$state.isOperator ? 'system' : 'team'}
							hasUpdates={$state.updates}
							hasService={$state.service}
							onChannel={handleChannelList}
							onPlayersite={handlePlayersite}
							onSettings={handleSettings}
							onLogout={handleLogout}
						/>
					</Grid>
					<Grid item className={classes.MainContent}>
						<Grid container className="MainContent-container" justifyContent="center" alignItems="center" spacing={0}>
							<Grid item sm={1}></Grid>
							<Grid item xs={12} sm={10} className="MainContent-item">
								{view}
							</Grid>
							<Grid item sm={1}></Grid>
						</Grid>
					</Grid>
				</Grid>
				<Snackbar
					anchorOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
					open={$snack.open}
					autoHideDuration={6000}
					onClose={handleCloseSnack}
				>
					<Alert variant="filled" elevation={6} onClose={handleCloseSnack} severity={$snack.severity}>
						{$snack.message}
					</Alert>
				</Snackbar>
				{expand && (
					<ChannelList
						open={$channelList.open}
						channels={$channelList.channels}
						channelid={$channelList.channelid}
						atChannelLimit={$channelLimit.atLimit}
						onClose={handleCloseChannelList}
						onClick={handleSelectChannel}
						onAdd={handleAddChannel}
						onState={handleStateChannel}
					/>
				)}
				{expand && $changelog.open && (
					<Changelog open={$changelog.open} onClose={handleCloseChangelog} current={$changelog.current} previous={$changelog.previous} />
				)}
			</NotifyProvider>
		</I18n>
	);
}

RestreamerUI.defaultProps = {
	address: '',
};
